/* eslint-disable react-hooks/exhaustive-deps */
import { Box } from '@chakra-ui/react';
import { useVendorNameConstraints, validateNotContainsEmail } from '@melio/ap-domain';
import { Form, Group, NakedButton, SectionBanner, useMelioForm } from '@melio/penny';
import { useAnalytics, useTrackAnalyticsOnFailedFormSubmit, useTrackFirstKeyPress } from '@melio/platform-analytics';
import { AccountingPlatform, AccountingPlatformCategory } from '@melio/platform-api';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { useMelioIntl } from '@melio/platform-i18n';
import { FileType, useConfig } from '@melio/platform-provider';
import { forwardRef } from '@melio/platform-utils';
import { defaults } from 'lodash';
import { useEffect, useState } from 'react';
import { date, mixed, object, SchemaOf, string } from 'yup';

import { FormWidgetProps } from '../../types';
import { AccountingPlatformCategorySelectWidget, VendorSelectWidget } from '../form-controls';
import { useAccountingPlatformBillCategoryLabel } from '../funding-sources';

export type AddInstantBillFormWidgetFields = {
  billAmount: string;
  billNumber: string;
  dueDate: Date;
  invoice?: File;
  note: string;
  categoryId?: AccountingPlatformCategory['id'];
} & ({ vendorId: string } | { vendorName: string });

export type AddInstantBillFormWidgetProps = FormWidgetProps<AddInstantBillFormWidgetFields> & {
  activeAccountingPlatform?: AccountingPlatform;
  inputVendorAsFreeText?: boolean;
};

const getDoesBillRequireInvoiceByAmount = (billAmount: number, threshold: number): boolean => billAmount >= threshold;

export const useSchema = (inputVendorAsFreeText?: boolean) => {
  const { formatMessage, formatCurrency } = useMelioIntl();
  const { fileSizeLimit, fileAllowedFormats } = useConfig().settings;
  const [vendorNameConstraintsEnabled] = useFeature(FeatureFlags.PlatformVendorNameConstraints, false);

  return object().shape({
    vendorName: inputVendorAsFreeText
      ? string()
          .required(formatMessage('widgets.addInstantBillForm.vendorName.validation.required'))
          .test(
            'not-contains-email',
            formatMessage('form.vendorSelect.constraints.email'),
            validateNotContainsEmail({ isEnabled: vendorNameConstraintsEnabled })
          )
          .nullable()
      : string().nullable(),
    vendorId: !inputVendorAsFreeText
      ? string().required(formatMessage('widgets.addInstantBillForm.vendorName.validation.required')).nullable()
      : string().nullable(),

    billAmount: string()
      .required(formatMessage('widgets.addInstantBillForm.billAmount.validation.required'))
      .test(
        'greaterThan',
        formatMessage('widgets.addInstantBillForm.billAmount.validation.greaterThan', {
          minValue: formatCurrency(0),
        }),
        (value = '') => parseFloat(value) > 0
      ),
    billNumber: string(),
    invoice: mixed<File>()
      .nullable()
      .test(
        'is-file-size-below-limit',
        formatMessage('widgets.addInstantBillForm.invoice.validation.fileSize'),
        (invoice) => {
          if (invoice) {
            return invoice?.size <= fileSizeLimit;
          }
          return true;
        }
      )
      .test(
        'is-file-format-allowed',
        formatMessage('widgets.addInstantBillForm.invoice.validation.fileFormat'),
        (invoice) => {
          if (invoice) {
            return fileAllowedFormats.includes(invoice.type?.split('/')[1] as FileType);
          }
          return true;
        }
      ),
    dueDate: date().nullable(),
    note: string(),
    categoryId: string().optional(),
  }) as SchemaOf<AddInstantBillFormWidgetFields>;
};

export const AddInstantBillFormWidget = forwardRef<AddInstantBillFormWidgetProps, 'form'>(
  (
    {
      onSubmit,
      defaultValues: _defaultValues,
      isDisabled,
      isSaving,
      onSubmissionStateChange,
      activeAccountingPlatform,
      inputVendorAsFreeText,
      ...props
    },
    ref
  ) => {
    const showCategorySelect = !!activeAccountingPlatform;
    const accountingPlatformBillCategoryLabel = useAccountingPlatformBillCategoryLabel(
      activeAccountingPlatform?.accountingSlug
    );
    const [doesBillRequireInvoice, setDoesBillRequireInvoice] = useState<boolean>(false);
    const [shouldShowAdditionalBillDetails, setShouldShowAdditionalBillDetails] = useState<boolean>(false);
    const defaultValues = defaults(_defaultValues, {
      vendorId: '',
      billAmount: '',
      billNumber: '',
      note: '',
    });
    const { track } = useAnalytics();
    const { formatMessage, formatCurrency } = useMelioIntl();
    const { requireInvoiceForBillsAmountThreshold } = useConfig().settings;
    const [isVendorNameFreeTextBlurred, setIsVendorNameFreeTextBlurred] = useState(false);

    const { formProps, registerField, watch, formState } = useMelioForm<AddInstantBillFormWidgetFields>({
      onSubmit,
      schema: useSchema(inputVendorAsFreeText),
      defaultValues,
      isSaving,
      onSubmissionStateChange,
      subscribeToDefaultValuesChanges: true,
    });
    const { fileAllowedFormats } = useConfig().settings;

    useEffect(() => {
      setDoesBillRequireInvoice(
        getDoesBillRequireInvoiceByAmount(Number(watch('billAmount')), requireInvoiceForBillsAmountThreshold)
      );
    }, [watch('billAmount'), requireInvoiceForBillsAmountThreshold]);

    const handleAddBillDetailsClick = () => {
      track('ExpandBillDetails', 'Clicked');
      setShouldShowAdditionalBillDetails(true);
    };

    useTrackAnalyticsOnFailedFormSubmit(formState, 'AddBillInfo', 'SavedAndContinued');

    const { onFieldKeyDown: onVendorSelectKeyDown, onFieldFocus: onVendorSelectFocus } = useTrackFirstKeyPress(
      'FillVendorDetails',
      'Submitted'
    );

    const { onFieldKeyDown: onAmountKeyDown, onFieldFocus: onAmountFocus } = useTrackFirstKeyPress(
      'FillBillAmount',
      'Submitted'
    );

    const inputProps = {
      colSpan: 2,
      labelProps: {
        label: formatMessage('widgets.addInstantBillForm.vendorName.label'),
      },
      placeholder: formatMessage('widgets.addInstantBillForm.vendorName.placeholder'),
      onKeyDown: onVendorSelectKeyDown,
      onFocus: onVendorSelectFocus,
    };

    const vendorNameInputProps = registerField('vendorName');

    const { vendorNameBannerDescription, vendorNameConstraintType } = useVendorNameConstraints({
      vendorName: watch('vendorName'),
      vendorNameFieldErrorType: vendorNameInputProps?.error?.type,
      vendorNameFreeTextInputProps: inputVendorAsFreeText ? { isBlurred: isVendorNameFreeTextBlurred } : undefined,
      disabled: !inputVendorAsFreeText,
      isFormSubmitted: formState.isSubmitted,
    });

    return (
      <Form
        columns={2}
        data-component="AddInstantBillFormWidget"
        ref={ref}
        {...formProps}
        {...props}
        isDisabled={isDisabled || formProps.isDisabled}
      >
        {inputVendorAsFreeText ? (
          <Form.ContentBox colSpan={2}>
            <Group spacing="xs" variant="vertical">
              <Form.TextField
                onBlur={() => setIsVendorNameFreeTextBlurred(true)}
                {...inputProps}
                {...vendorNameInputProps}
              />
              {vendorNameBannerDescription && (
                <SectionBanner
                  data-testid={`company-name-suggestion-${vendorNameConstraintType}`}
                  description={vendorNameBannerDescription}
                />
              )}
            </Group>
          </Form.ContentBox>
        ) : (
          <VendorSelectWidget {...inputProps} {...registerField('vendorId')} />
        )}
        <Form.AmountField
          colSpan={2}
          labelProps={{ label: formatMessage('widgets.addInstantBillForm.billAmount.label') }}
          {...registerField('billAmount')}
          onKeyDown={onAmountKeyDown}
          onFocus={onAmountFocus}
          {...(doesBillRequireInvoice && {
            helperTextProps: {
              label: formatMessage('widgets.addInstantBillForm.billAmount.helperText', {
                threshold: formatCurrency(requireInvoiceForBillsAmountThreshold, 'USD', {
                  maximumFractionDigits: 0,
                  minimumFractionDigits: 0,
                }),
              }),
            },
          })}
        />
        {doesBillRequireInvoice && (
          <Form.FileUpload
            colSpan={2}
            placeholder={formatMessage('widgets.addInstantBillForm.invoice.placeholder')}
            acceptTypes={fileAllowedFormats}
            {...registerField('invoice')}
            aria-label="invoice"
          />
        )}
        {!shouldShowAdditionalBillDetails ? (
          <Box>
            <NakedButton
              variant="secondary"
              isDisabled={isSaving}
              onClick={handleAddBillDetailsClick}
              data-testid="add-bill-details-button"
              label={formatMessage('widgets.addInstantBillForm.addBillDetailsButton')}
            />
          </Box>
        ) : (
          <>
            {showCategorySelect && (
              <AccountingPlatformCategorySelectWidget
                activeAccountingPlatform={activeAccountingPlatform}
                labelProps={{
                  label: accountingPlatformBillCategoryLabel,
                }}
                colSpan={2}
                {...registerField('categoryId')}
              />
            )}
            <Form.TextField
              labelProps={{ label: formatMessage('widgets.addInstantBillForm.billNumber.label') }}
              placeholder={formatMessage('widgets.addInstantBillForm.billNumber.placeholder')}
              {...registerField('billNumber')}
            />
            <Form.DateField
              labelProps={{ label: formatMessage('widgets.addInstantBillForm.dueDate.label') }}
              placeholder={formatMessage('widgets.addInstantBillForm.dueDate.placeholder')}
              helperTextProps={{ label: formatMessage('widgets.addInstantBillForm.dueDate.helperText') }}
              {...registerField('dueDate')}
            />
            <Form.TextField
              colSpan={2}
              labelProps={{ label: formatMessage('widgets.addInstantBillForm.note.label') }}
              {...registerField('note')}
              placeholder={formatMessage('widgets.addInstantBillForm.note.placeholder')}
            />
          </>
        )}
      </Form>
    );
  }
);

AddInstantBillFormWidget.displayName = 'AddInstantBillFormWidget';
